import styled from 'styled-components';
import {
  Children,
  ComponentType,
  forwardRef,
  ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { StatusType, Tag } from '@ardoq/status-ui';
import { useResizeObserver } from '@ardoq/hooks';
import { ControlledPopover, useDelayedHover } from '@ardoq/popovers';
import { Stack } from '@ardoq/layout';
import { s4 } from '@ardoq/design-tokens';

const TagContainer = styled.div<{ $maxHeight: string }>`
  max-height: ${props => props.$maxHeight};
  width: 100%;
  padding: ${s4};
  gap: ${s4};
  overflow: hidden;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const BadgeContainer = styled.div`
  position: absolute;
`;

const isElementBelowTheContainer = (container: DOMRect, element: DOMRect) =>
  element.top >= container.bottom;

const isElementTooToCloseToTheRightBottomCorner = (
  container: DOMRect,
  element: DOMRect
) =>
  // `bottom - bottom < 10` already restricts this to the last visible row, so
  // the reservation works whether truncation happens on row 1 or a later row.
  container.bottom - element.bottom < 10 &&
  container.right - element.right < 80;

const getOverflowIndexFromContainer = (
  containerEl: HTMLDivElement,
  childCount: number
) => {
  const containerRect = containerEl.getBoundingClientRect();
  const children = Array.from(containerEl.children).slice(0, childCount); // don't include the badge

  // No tag is wrapped below the visible area, so nothing is hidden and no badge
  // is needed. Returning early also skips the right-corner badge-space
  // reservation, which would otherwise hide fitting tags on a full single row.
  const hasChildBelowContainer = children.some(child =>
    isElementBelowTheContainer(containerRect, child.getBoundingClientRect())
  );
  if (!hasChildBelowContainer) return -1;

  return children.findIndex((child, i) => {
    const childRect = child.getBoundingClientRect();

    return (
      isElementBelowTheContainer(containerRect, childRect) ||
      (i < childCount - 1 &&
        isElementTooToCloseToTheRightBottomCorner(containerRect, childRect))
    );
  });
};

type OverflowIndexState = {
  overflowIndex: number;
  badgeLeftPosition: number;
  badgeTopPosition: number;
};

const updateChildVisibility = (
  children: Element[],
  overflowIndex: number,
  childCount: number
) =>
  children
    .slice(0, childCount) // dont include the badge
    .forEach((child, i) => {
      if (child && 'style' in child && typeof child.style === 'object') {
        if (i < overflowIndex || overflowIndex === -1) {
          (child as HTMLDivElement).style.visibility = 'visible';
        } else {
          (child as HTMLDivElement).style.visibility = 'hidden'; // we do this to hide the child element if it's under the badge
        }
      }
    });

export type OverflowBadgeProps = { hiddenCount: number };

const DefaultOverflowBadge = ({ hiddenCount }: OverflowBadgeProps) => (
  <Tag statusType={StatusType.DEFAULT} label={`+${hiddenCount} more`} />
);

export const TagContainerWithTruncation = forwardRef<
  { closePopover: VoidFunction },
  {
    children: ReactNode;
    maxHeight?: string;
    OverflowBadge?: ComponentType<OverflowBadgeProps>;
  }
>(
  (
    { children, maxHeight = '63px', OverflowBadge = DefaultOverflowBadge },
    ref
  ) => {
    const childCount = Children.count(children);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const lastVisibleChildRightPosition = useRef<number>(0);
    const { isOpen, handleMouseEnter, handleMouseLeave, handleClose } =
      useDelayedHover();
    const popoverId = useId();

    useImperativeHandle(ref, () => ({ closePopover: handleClose }), [
      handleClose,
    ]);

    const [
      { overflowIndex, badgeLeftPosition, badgeTopPosition },
      setOverflowState,
    ] = useState<OverflowIndexState>({
      badgeLeftPosition: 0,
      badgeTopPosition: 0,
      overflowIndex: childCount,
    });

    useResizeObserver(containerRef, () => {
      if (!containerRef.current) return;
      const newOverflowIndex = getOverflowIndexFromContainer(
        containerRef.current,
        childCount
      );
      const childElements = Array.from(containerRef.current.children);
      const newLastVisibleChildPosition =
        newOverflowIndex !== -1
          ? childElements[newOverflowIndex - 1]?.getBoundingClientRect()
          : null;

      const containerRect = containerRef.current.getBoundingClientRect();
      // Place the badge immediately after the last visible tag, aligned to that
      // tag's row, so it sits inline regardless of how many rows are shown.
      const newBadgeLeftPosition = newLastVisibleChildPosition
        ? newLastVisibleChildPosition.right - containerRect.left
        : 0;
      const newBadgeTopPosition = newLastVisibleChildPosition
        ? newLastVisibleChildPosition.top - containerRect.top
        : 0;
      if (
        overflowIndex !== newOverflowIndex ||
        newBadgeLeftPosition !== badgeLeftPosition ||
        newBadgeTopPosition !== badgeTopPosition
      ) {
        requestAnimationFrame(() =>
          setOverflowState({
            overflowIndex: newOverflowIndex,
            badgeLeftPosition: newBadgeLeftPosition,
            badgeTopPosition: newBadgeTopPosition,
          })
        );
        updateChildVisibility(childElements, newOverflowIndex, childCount);
      }
      if (newLastVisibleChildPosition)
        lastVisibleChildRightPosition.current =
          newLastVisibleChildPosition?.right ?? 0;
    });

    const hasOverflow = overflowIndex !== -1 && overflowIndex < childCount;

    useEffect(() => {
      if (!hasOverflow && isOpen) {
        handleClose();
      }
    }, [hasOverflow, isOpen, handleClose]);

    return (
      <TagContainer $maxHeight={maxHeight} ref={containerRef}>
        {children}
        {hasOverflow && (
          <ControlledPopover
            id={popoverId}
            isOpen={isOpen}
            onClose={handleClose}
            onClickOutside={handleClose}
            onMouseEnterPopover={handleMouseEnter}
            onMouseLeavePopover={handleMouseLeave}
            content={
              <Stack gap="xsmall">
                {Children.toArray(children).slice(overflowIndex)}
              </Stack>
            }
          >
            <BadgeContainer
              style={{
                top: `${badgeTopPosition}px`,
                left: `${badgeLeftPosition + 4}px`,
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {/* +4px buffer to emulate gap */}
              <OverflowBadge hiddenCount={childCount - overflowIndex} />
            </BadgeContainer>
          </ControlledPopover>
        )}
      </TagContainer>
    );
  }
);
